import { useComputed, useSignal } from "@preact/signals";
import type {
  SuggestionKeyDownProps,
  SuggestionProps,
} from "@tiptap/suggestion";
import { createPortal } from "preact/compat";
import { useCallback, useEffect, useMemo, useRef } from "preact/hooks";
import {
  getCachedMembers,
  type MentionSuggestion,
  prefetchMembersForMention,
} from "../../utils/member-cache";
import { MentionDropdown } from "./mention-dropdown";
import {
  decodeMentionsToHtml,
  encodeMentionsFromHtml,
} from "./rich-text-mention-extension";
import { TiptapEditor } from "./tiptap-editor";
import { createTiptapMention } from "./tiptap-mention-config";

export { prefetchMembersForMention };

type MentionRichTextEditorProps = {
  value: string;
  onChange: (value: string) => void;
  workspaceId: string;
  placeholder?: string;
  autoFocus?: boolean;
};

// Dropdown dimensions for viewport clamping
const DROPDOWN_HEIGHT = 192; // max-h-48
const DROPDOWN_WIDTH = 256; // w-64

export function MentionRichTextEditor({
  value,
  onChange,
  workspaceId,
  placeholder,
  autoFocus,
}: MentionRichTextEditorProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Autocomplete state
  const isOpen = useSignal(false);
  const suggestions = useSignal<MentionSuggestion[]>([]);
  const loading = useSignal(false);
  const selectedIndex = useSignal(0);
  const includeEveryone = useSignal(false);
  const dropdownPosition = useSignal({ top: 0, left: 0 });

  // Store the command callback from Tiptap suggestion
  const commandRef = useRef<
    ((props: { id: string; label: string }) => void) | null
  >(null);

  // Combined suggestions including @everyone
  const allSuggestions = useComputed(() => {
    const result: MentionSuggestion[] = [];
    if (includeEveryone.value) {
      result.push({
        memberId: "everyone",
        name: "everyone",
        profilePictureUrl: null,
        isEveryone: true,
      });
    }
    return [...result, ...suggestions.value];
  });

  // Cached member list for client-side filtering
  const allMembers = useSignal<MentionSuggestion[]>([]);
  const pendingSelect = useSignal(false);

  // Prefetch members on mount for instant suggestions
  useEffect(() => {
    const result = getCachedMembers(workspaceId);
    if (Array.isArray(result)) {
      allMembers.value = result;
    } else {
      result.then((members) => {
        allMembers.value = members;
      });
    }
  }, [workspaceId]);

  /** Clamp dropdown position to viewport */
  function clampPosition(top: number, left: number) {
    let t = top;
    let l = left;
    if (t + DROPDOWN_HEIGHT > window.innerHeight) {
      // Position above the cursor rect instead
      t = t - DROPDOWN_HEIGHT - 8;
    }
    if (l + DROPDOWN_WIDTH > window.innerWidth) {
      l = window.innerWidth - DROPDOWN_WIDTH - 8;
    }
    return { top: t, left: l };
  }

  // Create mention extension with Tiptap suggestion plugin
  const mentionExtension = useMemo(() => {
    return createTiptapMention({
      char: "@",
      items: ({ query }) => {
        // Ensure members are loaded
        const result = getCachedMembers(workspaceId);
        if (!Array.isArray(result)) {
          loading.value = true;
          result.then((members) => {
            allMembers.value = members;
            loading.value = false;
          });
          return [];
        }
        allMembers.value = result;
        loading.value = false;

        // Filter
        const q = query.toLowerCase();
        let filtered: MentionSuggestion[];
        if (q.length === 0) {
          filtered = result;
        } else {
          filtered = result.filter((m) => m.name.toLowerCase().startsWith(q));
        }
        return filtered;
      },
      render: () => {
        return {
          onStart: (props: SuggestionProps) => {
            commandRef.current = props.command;
            isOpen.value = true;
            selectedIndex.value = 0;

            // Filter for the initial query
            const q = (props.query || "").toLowerCase();
            suggestions.value = props.items as MentionSuggestion[];
            includeEveryone.value =
              q.length === 0 || "everyone".startsWith(q) || "all".startsWith(q);

            // Position dropdown (clamped to viewport)
            const rect = props.clientRect?.();
            if (rect) {
              dropdownPosition.value = clampPosition(
                rect.bottom + 4,
                rect.left,
              );
            }
          },
          onUpdate: (props: SuggestionProps) => {
            commandRef.current = props.command;
            const q = (props.query || "").toLowerCase();
            suggestions.value = props.items as MentionSuggestion[];
            includeEveryone.value =
              q.length === 0 || "everyone".startsWith(q) || "all".startsWith(q);
            selectedIndex.value = 0;

            const rect = props.clientRect?.();
            if (rect) {
              dropdownPosition.value = clampPosition(
                rect.bottom + 4,
                rect.left,
              );
            }
          },
          onKeyDown: (props: SuggestionKeyDownProps) => {
            const { event } = props;
            if (!isOpen.value || allSuggestions.value.length === 0)
              return false;

            if (event.key === "ArrowDown") {
              event.preventDefault();
              event.stopPropagation();
              selectedIndex.value =
                (selectedIndex.value + 1) % allSuggestions.value.length;
              return true;
            }
            if (event.key === "ArrowUp") {
              event.preventDefault();
              event.stopPropagation();
              selectedIndex.value =
                (selectedIndex.value - 1 + allSuggestions.value.length) %
                allSuggestions.value.length;
              return true;
            }
            if (event.key === "Enter" || event.key === "Tab") {
              event.preventDefault();
              event.stopPropagation();
              if (loading.value) {
                pendingSelect.value = true;
              } else {
                const selected = allSuggestions.value[selectedIndex.value];
                if (selected && commandRef.current) {
                  commandRef.current({
                    id: selected.memberId,
                    label: selected.name,
                  });
                  isOpen.value = false;
                }
              }
              return true;
            }
            if (event.key === "Escape") {
              event.preventDefault();
              event.stopPropagation();
              isOpen.value = false;
              return true;
            }
            return false;
          },
          onExit: () => {
            isOpen.value = false;
            commandRef.current = null;
          },
        };
      },
    });
  }, [workspaceId]);

  // Auto-select first match when results arrive after pending Enter
  useEffect(() => {
    if (
      pendingSelect.value &&
      !loading.value &&
      allSuggestions.value.length > 0
    ) {
      pendingSelect.value = false;
      const selected = allSuggestions.value[0];
      if (selected && commandRef.current) {
        commandRef.current({ id: selected.memberId, label: selected.name });
        isOpen.value = false;
      }
    }
  }, [loading.value, allSuggestions.value]);

  // Handle click outside to close dropdown
  useEffect(() => {
    if (!isOpen.value) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        isOpen.value = false;
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen.value]);

  // Handle selection from dropdown
  const handleSelect = useCallback((suggestion: MentionSuggestion) => {
    if (commandRef.current) {
      commandRef.current({ id: suggestion.memberId, label: suggestion.name });
      isOpen.value = false;
    }
  }, []);

  // Handle value changes - encode mentions for storage
  const handleChange = useCallback(
    (html: string) => {
      const encoded = encodeMentionsFromHtml(html);
      onChange(encoded);
    },
    [onChange],
  );

  // Decode mentions for display
  const displayValue = useMemo(() => decodeMentionsToHtml(value), [value]);

  return (
    <div class="relative">
      <TiptapEditor
        key={workspaceId}
        value={displayValue}
        onChange={handleChange}
        placeholder={placeholder}
        autoFocus={autoFocus}
        extensions={[mentionExtension]}
      />

      {/* Mention autocomplete dropdown — portaled to body to escape overflow-hidden ancestors */}
      {isOpen.value &&
        createPortal(
          <MentionDropdown
            dropdownRef={dropdownRef}
            suggestions={allSuggestions.value}
            selectedIndex={selectedIndex.value}
            loading={loading.value}
            onSelect={handleSelect}
            onHover={(index) => {
              selectedIndex.value = index;
            }}
            class="fixed w-64"
            style={{
              top: `${dropdownPosition.value.top}px`,
              left: `${dropdownPosition.value.left}px`,
            }}
          />,
          document.body,
        )}
    </div>
  );
}
